import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Breadcrumbs, CircularProgress, Typography } from '@mui/material';

import RightArrowIcon from '@mui/icons-material/ChevronRightRounded';

import FieldBox from 'shared/components/widgets/field-box';
import FieldBoxProperty from 'shared/components/widgets/field-box-property';

import { ROUTES } from 'constants/routes';
import Button from 'shared/components/widgets/button';
import Input from 'shared/components/widgets/input';
import Autocomplete from 'shared/components/widgets/autocomplete';
import { Controller, useForm } from 'react-hook-form';
import AutocompleteContainer from 'shared/components/widgets/autocomplete-container';
import InputContainer from 'shared/components/widgets/input-container';
import { systemManagementDetailService } from '@services/private/system/management/management-detail.service';
import { useEffect, useState } from 'react';
import { is200xRange } from '@utils/utilities';
import RouteLink from 'shared/components/widgets/route-link';
import { systemManagementUpdateService } from '@services/private/system/management/management-update-service';
import { EmailDomain } from 'types';
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup';
import { validateEmail } from 'shared/helpers/validateEmail';

type ManagementDetailData = {
  adminConfigEnv: {
    cssJsVersion: string;
  } | null;

  admMstDetail: {
    admEmail: string;
    admId: string;
    admName: string;
    admSeq: string;
    admSt: string;
    regAdmName: string;
    regDt: string;
    updAdmName: string;
    updDt: string;
  } | null;
};

const InformationQuerySchema = yup.object({
  admName: yup.string().required('관리자 이름은 필수 항목입니다').min(3, '최소 3자 이상 입력해 주세요'),
  emailName: yup.string(),
  emailDomain: yup.string(),
  customDomain: yup.string(),
  admSt: yup.string(),
  newDomain: yup.string()
});

const ChangeInformationPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [errorEmail, setErrorEmail] = useState('')
  const [isLoading, setLoading] = useState<boolean>()
  const [managementDetailData, setManagementDetailData] =
    useState<ManagementDetailData>({
      adminConfigEnv: null,
      admMstDetail: null,
    });

  const { control, setValue, handleSubmit, watch, formState: { errors }, trigger } = useForm({
    defaultValues: {
      admName: state?.admName || '',
      emailName: state?.admEmail.split('@')[0] || '',
      emailDomain: state?.admEmail.split('@')[1] || '',
      admSt: state?.admSt || '',
      customDomain: state?.admEmail.split('@')[1] || '',
      newDomain: state?.admEmail.split('@')[1] || '',
    },
    resolver: yupResolver(InformationQuerySchema)
  });

  const admStFilterData = [
    { label: '신청', value: '10' },
    { label: '심사중', value: '20' },
    { label: '반려', value: '30' },
    { label: '정상', value: '50' },
  ];

  const domainList = [
    { label: '직접입력', value: '' },
    { label: 'gmail.com (G메일)', value: 'gmail.com' },
    { label: 'naver.com  (네이버)', value: 'naver.com' },
    { label: 'empal.com (엠팔)', value: 'empal.com' },
    { label: 'nate.com (네이트)', value: 'nate.com' },
    { label: 'hanmail.net  (다음)', value: 'hanmail.net' },
    { label: 'kakao.com  (카카오)', value: 'kakao.com' },
    { label: 'markany.com  (마크애니)', value: 'markany.com' },
  ];


  const getSystemManagementDetail = async () => {
    const { response, statusCode } = await systemManagementDetailService(
      state?.admSeq,
    );

    if (response?.code === '1' && is200xRange(statusCode)) {
      handleListData([
        { key: 'adminConfigEnv', value: response?.result?.adminConfigEnv },
        { key: 'admMstDetail', value: response?.result?.admMstDetail },
      ]);
    }
  };

  const updateSystemManagement = async (data: {
    admSeq: string;
    admName: string;
    emailName: string;
    emailDomain: EmailDomain;
    admSt: '10' | '20' | '30' | '50';
  }) => {
    const { response, statusCode } = await systemManagementUpdateService(data);
    if (response?.code === '1' && is200xRange(statusCode)) {
      navigate(`${ROUTES.SYSTEM.MANAGER.path}?refetch=true`);
    }
  };

  const handleListData = (params) => {
    setManagementDetailData((prevListData) => {
      const newListData = { ...prevListData };
      params.forEach((item) => {
        newListData[item.key] = item.value;
      });
      return newListData;
    });
  };

  const onSubmit = async (data: any) => {
    const { admName, emailName, customDomain, admSt } = data;
    setLoading(true)
    await updateSystemManagement({
      admSeq: managementDetailData?.admMstDetail?.admSeq ?? '',
      admName,
      emailName,
      emailDomain: customDomain,
      admSt,
    });
    setLoading(false)
  };

  const keyword = watch('customDomain');

  useEffect(() => {
    getSystemManagementDetail();
  }, []);

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <Box marginBottom="18px">
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<RightArrowIcon sx={{ color: 'var(--neutral-800)' }} />}
        >
          <RouteLink to={ROUTES.SYSTEM.MANAGER.path}>Home</RouteLink>
          <RouteLink to={ROUTES.SYSTEM.MANAGER.path}>시스템</RouteLink>
          <RouteLink to={ROUTES.SYSTEM.MANAGER.path}>관리자</RouteLink>

          <RouteLink to={ROUTES.SYSTEM.MANAGER.path} isActive>
            정보 수정
          </RouteLink>
        </Breadcrumbs>
      </Box>
      <Box marginBottom="20px">
        <Typography variant="h6" color="var(--base-black)" fontWeight={700}>
          관리자
        </Typography>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" gap={6} marginBottom="50px">
          <FieldBox>
            {/* ADMINISTRATOR ID */}
            <FieldBoxProperty
              name="* 관리자 아이디"
              type="text"
              value={state?.admId ?? '--'}
            />
            {/* ADMINISTRATOR NAME */}
            <FieldBoxProperty
              name="* 관리자 이름"
              value={
                <Controller
                  name="admName"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputContainer type="form">
                        <Input
                          {...field}
                          placeholder=""
                          defaultValue={field.value ?? '--'}
                          showErrorMsg={!!errors.admName}
                          errorMessage={errors.admName && errors.admName.message}
                        />
                      </InputContainer>
                    );
                  }}
                />
              }
            />
            {/* EMAIL */}
            <FieldBoxProperty
              name="* 이메일"
              value={
                <Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Controller
                      name="emailName"
                      control={control}
                      render={({ field }) => {
                        return (
                          <Input
                            {...field}
                            sx={{
                              minWidth: '260px',
                              maxWidth: '260px',
                            }}
                            placeholder=""
                            defaultValue={field.value ?? '--'}
                          />
                        );
                      }}
                    />

                    <Typography variant="body1" color="var(--base-black)">
                      @
                    </Typography>
                    <Controller
                      name="customDomain"
                      control={control}
                      render={({ field }) => {
                        return (
                          <Input
                            {...field}
                            keyword={keyword}
                            clearAction={() => {
                              setValue(
                                'customDomain',
                                state?.admEmail.split('@')[1] || '',
                              );
                            }}
                            sx={{
                              minWidth: '260px',
                              maxWidth: '260px',
                            }}
                            placeholder="검색어를 입력해 주세요."
                            onChange={(e) => {
                              setErrorEmail('')
                              const val = e.currentTarget.value
                              const domain = domainList.find((domain) => domain.value === val)
                              field.onChange(val)
                              if (!domain) {
                                setValue('newDomain', val)
                              } else {
                                console.log(val)
                                setValue('emailDomain', domain.value)
                              }
                            }}
                          //   onBlur={(e) => {
                          //     if (e.target.value === '') {
                          //       setValue(
                          //         'customDomain',
                          //         state?.admEmail.split('@')[1] || '',
                          //       );
                          //     } else {
                          //       const isExist = domainList.findIndex((item) => {
                          //         return item.value === e.target.value;
                          //       });
                          //       if (isExist === -1) {
                          //         setValue('emailDomain', '--');
                          //       } else {
                          //         setValue('emailDomain', e.target.value);
                          //       }
                          //     }
                          //   }}
                          />
                        );
                      }}
                    />

                    <Controller
                      name="emailDomain"
                      control={control}
                      render={({ field }) => {
                        return (
                          <Autocomplete
                            {...field}
                            options={domainList}
                            value={
                              domainList.find((domain) => (
                                domain.value === field?.value
                              ))?.value ?? ''
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.value === value.value
                            }
                            inputStyle={{ width: '250px' }}
                            onChange={(_, newValue, reason) => {
                              setErrorEmail('')
                              field.onChange(newValue?.value);
                              // @ts-ignore
                              if (newValue?.label === '직접입력') {
                                setValue('customDomain', '');
                              }
                              else {
                                setValue('customDomain', newValue?.value as string);
                                if (reason === 'clear') {
                                  setValue(
                                    'emailDomain',
                                    state?.admEmail.split('@')[1],
                                  );
                                  setValue(
                                    'customDomain',
                                    state?.admEmail.split('@')[1],
                                  );
                                }
                              }
                            }}
                          />
                        );
                      }}
                    />
                  </Box>
                  {errorEmail && (
                    <Typography sx={{ marginTop: '7px' }} color={'var(--danger-500)'}>
                      {errorEmail}
                    </Typography>
                  )}
                </Box>
              }
            />

            {/* ADMINISTRATOR STATUS */}
            <FieldBoxProperty
              name="* 관리자 상태"
              value={
                <AutocompleteContainer type="form">
                  <Controller
                    name="admSt"
                    control={control}
                    render={({ field }) => {
                      return (
                        <Autocomplete
                          {...field}
                          options={admStFilterData}
                          defaultValue={
                            admStFilterData.find(
                              (el) => el.value === state?.admSt,
                            ) ?? {
                              label: '신청',
                              value: '10',
                            }
                          }
                          value={field?.value || state?.admSt || '10'}
                          isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                          }
                          onChange={(_, newValue, reason) => {
                            field.onChange(newValue?.value);
                            if (reason === 'clear') {
                              field.onChange('10');
                            }
                          }}
                        />
                      );
                    }}
                  />
                </AutocompleteContainer>
              }
            />
          </FieldBox>
        </Box>
        <Box>
          <Box display="flex" gap="14px">
            {/* CANCEL */}
            <Button
              disabled={isLoading}
              variant="outlined"
              color="secondary"
              sx={{ minWidth: '112px' }}
              onClick={() => navigate(ROUTES.SYSTEM.MANAGER.path)}
            >
              취소
            </Button>
            {/* SAVE */}
            <Button
              disabled={isLoading}
              variant="contained"
              color="primary"
              sx={{ minWidth: '112px' }}
              type="button"
              onClick={async () => {
                const fullEmail = `${watch('emailName')}@${watch('customDomain')}`
                trigger()
                setErrorEmail('')
                if (!validateEmail(fullEmail)) {
                  return setErrorEmail('이메일이 올바르지 않습니다')
                }
                handleSubmit(onSubmit)()
              }}
            >
              {isLoading ?
                <CircularProgress size={18} color='secondary' />
                :
                ' 저장하기'
              }
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChangeInformationPage;
